import { BreadcrumbItem, Breadcrumbs } from "@vds/breadcrumbs";
import { Loader } from "@vds/loaders";
import { Tab, Tabs } from "@vds/tabs";
import { Title } from "@vds/typography";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  fetchNotificationGroupsEndPoint,
  fetchNotificationUsersEndPoint,
  getAllInviteesEndPoint,
} from "../../configuration";
import axiosInstance from "../../global/axiosInterceptor";
import { TAB_NAMES, USER_MANAGEMENT_ERROR_MESSAGES, BANNER_TYPES } from "../../global/constants/constants";
import GmtToLocal from "../../global/utility/GmtToLocal";
import { getSortedListOfRecords } from "../../global/utility/tableUtils";
import { handleApiError } from "../../global/utility/Utils";
import {
  setUserManagementUsers,
  setUserManagementGroups,
  setUserManagementPendingInvites,
} from "../../store/actions";
import PendingUsersTab from "../Users/PendingUsersTab";
import NotificationGroups from "./NotificationGroups";
import NotificationUsers from "./NotificationUsers";
import { usePaginationReset } from "../../hooks/usePaginationReset";

const defaultGroupFormFields = {
  groupName: "",
  groupId: "",
  groupEmail: "",
  groupDescription: "",
  users: [],
};

const UserGroups = () => {
  const navigate = useNavigate();
  const isFromOwnDetailPage = usePaginationReset(["UserGroups", "UserTable", "PendingInvites"]);
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeSession } = useSelector(
    (state) => state.sessionReducer.activeSession
  );
  const cleanMDN = (mdn) => {
    if (mdn) {
      const phoneDigits = mdn.replace(/\D/g, "");
      if (phoneDigits.length > 10) {
        return phoneDigits.slice(-10);
      }
      return phoneDigits;
    }
    return "";
  };
  const { siteTagList } = useSelector((state) => state?.sitesReducer);
  const { roleList } = useSelector((state) => state?.userRoleReducer);
  const cachedData = useSelector((state) => state?.userManagementReducer);
  const location = useLocation();
  const getBackCurrentTab = location?.state?.tabName;

  // Convert tab name to URL-friendly format (e.g., "User Groups" -> "user-groups")
  const tabToUrlParam = (tabName) => {
    return encodeURIComponent(tabName.replaceAll(" ", "-").toLowerCase());
  };

  // Convert URL param back to tab name (e.g., "user-groups" -> "User Groups")
  const urlParamToTab = (param) => {
    if (!param) return null;
    const decoded = decodeURIComponent(param);
    const validTabs = Object.values(TAB_NAMES);
    return validTabs.find(tab => tabToUrlParam(tab) === decoded) || null;
  };

  const tabFromUrl = urlParamToTab(searchParams.get("tab"));
  const initialTab =
    tabFromUrl
      ? tabFromUrl
      : getBackCurrentTab === TAB_NAMES.USER_GROUPS
        ? TAB_NAMES.USER_GROUPS
        : getBackCurrentTab === TAB_NAMES.PENDING_INVITES
          ? TAB_NAMES.PENDING_INVITES
          : TAB_NAMES.USERS;

  const [notificationGroupList, setNotificationGroupList] = useState(cachedData?.userGroups ?? []);
  const [notificationUsersList, setNotificationUsersList] = useState(cachedData?.users ?? []);
  const [pendingInvitesList, setPendingInvitesList] = useState(cachedData?.pendingInvites ?? []);
  const [refreshGroupsTable, setRefreshGroupsTable] = useState(false);
  const [refreshUsersTable, setRefreshUsersTable] = useState(false);
  const [userDataLoading, setUserDataLoading] = useState(!cachedData?.users?.length);
  const [groupDataLoading, setGroupDataLoading] = useState(!cachedData?.userGroups?.length);
  const [pendingInvitesLoading, setPendingInvitesLoading] = useState(!cachedData?.pendingInvites?.length);
  const [groupFormFields, setGroupFormFields] = useState(
    defaultGroupFormFields
  );
  const [showNotificationPopup, setShowNotificationPopup] = useState({
    message: "",
    show: false,
    type: "",
    title: "",
  });
  const ref = useRef();

  const [pageLoading, setPageLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(initialTab);
  const [showNotification, setShowNotification] = useState(false);
  const [bannerType, setBannerType] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);
  const dispatch = useDispatch();
  const isInitialMount = useRef(true);

  const fetchNotificationGroups = async (showSpinner = true) => {
    try {
      if (showSpinner) setGroupDataLoading(true);
      const response = await axiosInstance.post(fetchNotificationGroupsEndPoint(), {
        accountName: activeSession.accountId,
        filter: {
          $sort: [
            {
              field: "createdon",
              order: "desc"
            }
          ]
        }
      });
      const notificationGroupResponse = response.data;
      sessionStorage.setItem(
        "userGroupsResponse",
        JSON.stringify(notificationGroupResponse)
      );
      let groupList = [];
      if (notificationGroupResponse?.length) {
        groupList = notificationGroupResponse.map((item) => {
          const createdon = GmtToLocal(item?.createdon);
          const lastupdated = GmtToLocal(item?.lastupdated);
          return {
            ...item,
            createdon,
            lastupdated,
          };
        });
        const sortedList = getSortedListOfRecords(groupList, "name");
        dispatch(setUserManagementGroups(sortedList));
        setNotificationGroupList(sortedList);
      } else {
        dispatch(setUserManagementGroups([]));
        setNotificationGroupList([]);
      }
      setPageLoading(false);
      setGroupDataLoading(false);
    } catch (error) {
      setPageLoading(false);
      setGroupDataLoading(false);
      console.error("Error loading user groups table:", error);
      const errorMessage = error?.response?.data?.error_description;
      handleApiError(
        BANNER_TYPES.WARNING,
        errorMessage
          ? errorMessage
          : USER_MANAGEMENT_ERROR_MESSAGES.FETCH_USER_GROUPS_FAILED
      );
      return [];
    }
  };

    useEffect(() => {
      if (!notificationUsersList || notificationUsersList.length === 0) return;
      if (!notificationGroupList || notificationGroupList.length === 0) {
        setNotificationUsersList((prevUsers) =>
          prevUsers.map((user) => ({ ...user, group: {} }))
        );
        return;
      }
      setNotificationUsersList((prevUsers) =>
        prevUsers.map((user) => ({
          ...user,
          group: notificationGroupList.length > 0
              ? notificationGroupList.find(
                  (group) => group?.foreignid === user?.foreignid
                ) || {}
              : {},
        }))
      );
    }, [notificationGroupList]);

  useEffect(() => {
    setPageLoading(false);
    // Show spinner only if no cached data from Redux
    fetchNotificationUser(!cachedData?.users?.length);
    fetchNotificationGroups(!cachedData?.userGroups?.length);
    fetchPendingInvites(!cachedData?.pendingInvites?.length);
  }, []);

  const fetchNotificationUser = async (showSpinner = true) => {
    if (showSpinner) setUserDataLoading(true);
    try {
      const response = await axiosInstance.post(
        fetchNotificationUsersEndPoint(),
        {
          accountName: activeSession.accountId,
          filter: {
            $expand: "roles",
          },
        }
      );
      const userResponse = response.data;

      const notificationUserResponse = userResponse?.map((user) => {
        return {
          ...user,
          mdn: cleanMDN(user.mdn),
        };
      });
      const sortedUsers = getSortedListOfRecords(notificationUserResponse, "firstname");
      dispatch(setUserManagementUsers(sortedUsers));
      setNotificationUsersList([...sortedUsers]);
      setUserDataLoading(false);
      setRefreshUsersTable(false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error_description;
      console.error("Error loading notification users:", error);
      handleApiError(
        BANNER_TYPES.WARNING,
        errorMessage
          ? errorMessage
          : USER_MANAGEMENT_ERROR_MESSAGES.FETCH_USERS_FAILED
      );
      setUserDataLoading(false);
      return [];
    }
  };

  useEffect(() => {
    if (location.state?.response) {
      setRefreshGroupsTable(false);
      setPageLoading(false);
    }
  }, [location.state]);

  useEffect(() => {
    let timer = "";
    window?.scrollTo({ top: 0, behaviour: "smooth" });
    if (showNotificationPopup?.show) {
      ref?.current?.scrollIntoView({ behavior: "smooth" });
      timer = setTimeout(() => {
        setShowNotificationPopup(null);
      }, 5000);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [showNotificationPopup?.show]);

  useEffect(() => {
    if (refreshGroupsTable || refreshUsersTable) {
      if (refreshUsersTable) {
        setRefreshGroupsTable(true);
        setNotificationUsersList([]);
      }
      fetchNotificationGroups();
      setRefreshGroupsTable(false);
    }
  }, [refreshGroupsTable, refreshUsersTable]);

  useEffect(() => {
    // Skip on initial mount - initial fetch useEffect handles it
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!activeSession?.accountId) return;

    if (currentTab === TAB_NAMES.PENDING_INVITES) {
      // Show spinner only if no cached data, otherwise background fetch
      fetchPendingInvites(!cachedData?.pendingInvites?.length);
    } else if (currentTab === TAB_NAMES.USERS) {
      fetchNotificationUser(!cachedData?.users?.length);
    } else if (currentTab === TAB_NAMES.USER_GROUPS) {
      fetchNotificationGroups(!cachedData?.userGroups?.length);
    }
  }, [currentTab, activeSession?.accountId]);

  // Sync tab state with URL search params (for browser back/forward navigation)
  useEffect(() => {
    const tabFromUrl = urlParamToTab(searchParams.get("tab"));
    if (tabFromUrl && tabFromUrl !== currentTab) {
      setCurrentTab(tabFromUrl);
    } else if (!tabFromUrl) {
      setSearchParams({ tab: tabToUrlParam(currentTab) }, { replace: true });
    }
  }, [searchParams]);

  const handleAddGroupSuccess = () => {
    setRefreshGroupsTable(true);
  };

  const extractDataArray = (responseData) => {
    if (!responseData) return [];
    const possibleArrays = [
      responseData.data,
      responseData.result, 
      responseData.items,
      responseData
    ];
    return possibleArrays.find(arr => Array.isArray(arr)) || [];
  };

  const fetchPendingInvites = async (showSpinner = true) => {
    if (!activeSession?.accountId) return;

    try {
      if (showSpinner) setPendingInvitesLoading(true);

      const response = await axiosInstance.post(getAllInviteesEndPoint(), {
        accountname: activeSession.accountId,
        filter: {
          $sort: [{ field: "createdon", order: "desc" }],
          $selection: "isaccepted eq 'false'",
          $querytotalcount: true
        }
      });

      const rawData = extractDataArray(response?.data);
      const cleanedData = rawData.map(invite => ({
        ...invite,
        mdn: cleanMDN(invite.mdn || invite.inviteemdn || invite.phone || invite.phonenumber || invite.contactnumber),
        inviteemdn: cleanMDN(invite.inviteemdn || invite.mdn || invite.phone || invite.phonenumber || invite.contactnumber)
      }));
      dispatch(setUserManagementPendingInvites(cleanedData));
      setPendingInvitesList(cleanedData);

    } catch (error) {
      const errorMessage = error?.response?.data?.error_description;
      console.error("Error fetching pending invites:", error);
      handleApiError(
        BANNER_TYPES.WARNING,
        errorMessage
          ? errorMessage
          : USER_MANAGEMENT_ERROR_MESSAGES.FETCH_PENDING_INVITES_FAILED
      );
      dispatch(setUserManagementPendingInvites([]));
      setPendingInvitesList([]);
    } finally {
      setPendingInvitesLoading(false);
    }
  };

  const getFieldValue = (originalData, ...fieldNames) => {
    const foundField = fieldNames.find(field => originalData?.[field]);
    return foundField ? originalData[foundField] : '';
  };

  const buildSiteInfo = (originalData, siteTagList) => {
    const siteIds = originalData?.assignment?.siteids || [];
    if (!siteIds.length || !siteTagList?.sites?.length) {
      return { siteDetails: [], siteNames: [], sitesDisplay: 'Not Available' };
    }
    
    const siteDetails = siteIds
      .map(siteId => siteTagList.sites.find(site => site.id === siteId))
      .filter(Boolean);
    
    const siteNames = siteDetails.map(site => site.name);
    const sitesDisplay = siteNames.length > 1 
      ? `Multiple Sites (${siteNames.length})` 
      : siteNames[0] || 'Not Available';
    
    return { siteDetails, siteNames, sitesDisplay };
  };

  const handleUserDetailsClick = (user) => {
    const originalData = user.originalData || user;

    const roleDetails = originalData?.assignment?.roleid && roleList?.length > 0
      ? roleList.find(role => role.id === originalData.assignment.roleid)
      : null;
    
    const { siteDetails, siteNames, sitesDisplay } = buildSiteInfo(originalData, siteTagList);
    
    const transformedUser = {
      firstname: getFieldValue(originalData, 'inviteefirstname', 'firstname'),
      lastname: getFieldValue(originalData, 'inviteelastname', 'lastname'),
      email: getFieldValue(originalData, 'inviteeemail', 'email', 'emailaddress', 'email_address'),
      mdn: cleanMDN(getFieldValue(originalData, 'inviteemdn', 'mdn', 'phone', 'phonenumber', 'contactnumber')),
      id: getFieldValue(originalData, 'id', 'foreignid', 'userid'),
      assignment: originalData?.assignment,
      roleid: originalData?.assignment?.roleid,
      siteids: originalData?.assignment?.siteids,
      roleDetails,
      roleName: roleDetails?.name || '',
      siteDetails,
      siteNames,
      sitesDisplay,
      tags: siteDetails.map(site => ({ id: site.id, name: site.name })),
      
      customdata: originalData?.customdata || {
        roles: roleDetails ? [{ id: roleDetails.id, name: roleDetails.name }] : []
      },
      
      originalData
    };
    
    navigate('/userdetails', {
      state: {
        user: transformedUser,
        ...transformedUser, 
        tabName: user.isActiveUser ? TAB_NAMES.USERS : TAB_NAMES.PENDING_INVITES,
        fromPage: "UserGroups", 
        previousTab: currentTab,
        siteTagList: siteTagList  
      }
    });
  };

  const handleTabClick = (tabName) => {
    setCurrentTab(tabName);
    setSearchParams({ tab: tabToUrlParam(tabName) }, { replace: true });
  };

  //reproduce error boundary error
  // useEffect(() => {
  //   dispatch(setPageLoading(runtime));
  // }, []);

  
  if (pageLoading) {
    return (
      <>
        <Loader active={pageLoading} fullscreen={false} />
      </>
    );
  }

  return (
    <>
      <Loader active={pageLoading} fullscreen={false} />

      <div id="test" style={{ display: "flex", marginBottom: "20px" }}>
        <Breadcrumbs surface="light">
          <BreadcrumbItem>
            <Link data-testid="userGroups_settingsLink" to="/settings" className="breadcrums-item_link">
              Settings
            </Link>
          </BreadcrumbItem>
          <BreadcrumbItem>{currentTab}</BreadcrumbItem>
        </Breadcrumbs>
      </div>
      <Title size="large" bold={false}>
        {currentTab}
      </Title>
      <div className="usergroups usergroups-tabs-container usergroups-tabs-styling">
        <Tabs orientation="horizontal" indicatorPosition="bottom" fillContainer={false}>
          <Tab label={TAB_NAMES.USERS} data-testid="userGroups_usersTab" onClick={() => handleTabClick(TAB_NAMES.USERS)} selected={currentTab === TAB_NAMES.USERS}>
            {currentTab === TAB_NAMES.USERS && (
              <NotificationUsers
                loading={userDataLoading || refreshUsersTable || refreshGroupsTable}
                notificationGroupList={notificationGroupList}
                notificationUsersList={notificationUsersList}
                setGroupFormFields={setGroupFormFields}
                defaultGroupFormFields={defaultGroupFormFields}
                setRefreshUsersTable={setRefreshUsersTable}
                setShowNotificationPopup={setShowNotificationPopup}
                setNotificationUsersList={setNotificationUsersList}
                setShowNotification={setShowNotification}
                showNotification={showNotification}
                bannerType={bannerType}
                setBannerType={setBannerType}
                userList={notificationUsersList}
              />
            )}
          </Tab>
          <Tab label={TAB_NAMES.USER_GROUPS} data-testid="userGroups_userGroupsTab" onClick={() => handleTabClick(TAB_NAMES.USER_GROUPS)} selected={currentTab === TAB_NAMES.USER_GROUPS}>
            {currentTab === TAB_NAMES.USER_GROUPS && (
              <NotificationGroups
                loading={groupDataLoading || refreshGroupsTable}
                notificationGroupList={notificationGroupList}
                notificationUsersList={notificationUsersList}
                setRefreshGroupsTable={setRefreshGroupsTable}
                setShowNotificationPopup={setShowNotificationPopup}
                handleAddGroupSuccess={handleAddGroupSuccess}
                setShowNotification={setShowNotification}
                showNotification={showNotification}
                bannerType={bannerType}
                setBannerType={setBannerType}
              />
            )}
          </Tab>
          <Tab
            label={`${TAB_NAMES.PENDING_INVITES}${pendingInvitesList?.length > 0 ? ` (${pendingInvitesList.length})` : ""}`}
            data-testid="userGroups_pendingInvitesTab"
            onClick={() => handleTabClick(TAB_NAMES.PENDING_INVITES)}
            selected={currentTab === TAB_NAMES.PENDING_INVITES}
          >
            {currentTab === TAB_NAMES.PENDING_INVITES && (
              <PendingUsersTab
                users={pendingInvitesList}
                usersList={notificationUsersList}
                onCountChange={() => {}}
                onUserDetailsClick={handleUserDetailsClick}
                onRefreshData={fetchPendingInvites}
                loading={pendingInvitesLoading}
              />
            )}
          </Tab>
        </Tabs>
      </div>
    </>
  );
};

export default UserGroups;
